/**
 * TableStatusTab - Floor management dashboard showing active tables
 * Shows table number, time seated, server, guests, order total, status
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Table, Users, Clock, DollarSign, Check, Coffee, 
  ChefHat, AlertCircle, RefreshCw, X, Eye, CreditCard, Trash2, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function TableStatusTab({ authHeaders }) {
  const [tables, setTables] = useState([]);
  const [summary, setSummary] = useState({ active_tables: 0, total_guests: 0, total_pending_revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [stuckTables, setStuckTables] = useState([]);
  const [showStuckTables, setShowStuckTables] = useState(false);
  const [clearingStuck, setClearingStuck] = useState(false);

  const fetchTableStatus = useCallback(async () => {
    try {
      const res = await api.get('/admin/tables/status');
      setTables(res.data.tables || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      console.error('Failed to fetch table status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch stuck tables (older than 24 hours)
  const fetchStuckTables = useCallback(async () => {
    try {
      const res = await api.get('/admin/stuck-tables?hours_old=12');
      setStuckTables(res.data.stuck_tables || []);
    } catch (err) {
      console.error('Failed to fetch stuck tables:', err);
    }
  }, []);

  // Clear all stuck tables
  const handleClearAllStuck = async () => {
    if (!confirm(`Clear ALL ${stuckTables.length} stuck tables? This will cancel their unpaid orders.`)) return;
    
    setClearingStuck(true);
    try {
      const res = await api.post('/admin/clear-stuck-tables?hours_old=12');
      toast.success(`Cleared ${res.data.cleared} stuck tables!`);
      setStuckTables([]);
      setShowStuckTables(false);
      fetchTableStatus();
    } catch (err) {
      toast.error('Failed to clear stuck tables: ' + (err.response?.data?.detail || err.message));
    } finally {
      setClearingStuck(false);
    }
  };

  // Clear a single stuck table
  const handleClearSingleTable = async (tableNumber) => {
    try {
      const res = await api.delete(`/admin/clear-table/${encodeURIComponent(tableNumber)}`);
      toast.success(`Table ${tableNumber}: ${res.data.message}`);
      setStuckTables(prev => prev.filter(t => t.table_number !== tableNumber));
      fetchTableStatus();
    } catch (err) {
      toast.error('Failed to clear table: ' + (err.response?.data?.detail || err.message));
    }
  };

  useEffect(() => {
    fetchTableStatus();
    fetchStuckTables();
    
    // Auto-refresh every 30 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchTableStatus();
        fetchStuckTables();
      }, 30000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchTableStatus, fetchStuckTables, autoRefresh]);

  const handleCloseTable = async (tableNumber) => {
    if (!confirm(`Close Table ${tableNumber} and mark all orders as paid?`)) return;
    
    try {
      const res = await api.post(`/admin/tables/${tableNumber}/close`);
      toast.success(res.data.message);
      fetchTableStatus();
      setSelectedTable(null);
    } catch (err) {
      toast.error('Failed to close table');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-300 dark:border-green-700';
      case 'preparing': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-700';
      case 'partially_ready': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-700';
      default: return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready': return <Check className="w-4 h-4" />;
      case 'preparing': return <ChefHat className="w-4 h-4" />;
      case 'partially_ready': return <Coffee className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getTimeColor = (minutes) => {
    if (minutes >= 90) return 'text-red-600 dark:text-red-400';
    if (minutes >= 60) return 'text-amber-600 dark:text-amber-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="table-status-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <Table className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">Floor Status</h2>
            <p className="text-muted-foreground text-sm">Live table overview</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh
          </label>
          <Button variant="outline" size="sm" onClick={fetchTableStatus}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <Table className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{summary.active_tables}</div>
              <div className="text-sm text-muted-foreground">Active Tables</div>
            </div>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
              <Users className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{summary.total_guests}</div>
              <div className="text-sm text-muted-foreground">Total Guests</div>
            </div>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">${summary.total_pending_revenue?.toFixed(2)}</div>
              <div className="text-sm text-muted-foreground">Pending Revenue</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stuck Tables Alert Panel */}
      {stuckTables.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-red-800 dark:text-red-200">
                  {stuckTables.length} Stuck Tables Detected
                </h3>
                <p className="text-sm text-red-600 dark:text-red-400">
                  Tables with unpaid orders older than 12 hours
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowStuckTables(!showStuckTables)}
              >
                {showStuckTables ? 'Hide' : 'View All'}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleClearAllStuck}
                disabled={clearingStuck}
              >
                {clearingStuck ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Clear All Stuck
              </Button>
            </div>
          </div>
          
          {showStuckTables && (
            <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
              {stuckTables.map(table => (
                <div
                  key={table.id}
                  className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-3 border border-red-200 dark:border-red-800"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 dark:bg-red-900/50 rounded-lg flex items-center justify-center font-bold text-red-700 dark:text-red-300">
                      {table.table_number}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        Table {table.table_number}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {table.age_hours} hours old • ${table.total?.toFixed(2) || '0.00'}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleClearSingleTable(table.table_number)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Table Grid */}
      {tables.length === 0 ? (
        <div className="bg-card rounded-2xl border border-border p-12 text-center">
          <Table className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Active Tables</h3>
          <p className="text-muted-foreground">Dine-in orders will appear here when servers send orders to the kitchen.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {tables.map(table => (
            <div
              key={table.table_number}
              onClick={() => setSelectedTable(table)}
              className={`bg-card rounded-xl border-2 p-4 cursor-pointer transition-all hover:shadow-lg ${getStatusColor(table.overall_status)}`}
              data-testid={`table-card-${table.table_number}`}
            >
              {/* Table Number */}
              <div className="flex items-center justify-between mb-3">
                <div className="text-2xl font-bold">{table.table_number}</div>
                <div className="flex items-center gap-1">
                  {getStatusIcon(table.overall_status)}
                </div>
              </div>
              
              {/* Guests */}
              <div className="flex items-center gap-2 text-sm mb-2">
                <Users className="w-4 h-4" />
                <span>{table.guest_count} guests</span>
              </div>
              
              {/* Time Seated */}
              <div className={`flex items-center gap-2 text-sm mb-2 ${getTimeColor(table.time_seated_minutes)}`}>
                <Clock className="w-4 h-4" />
                <span>{table.time_seated_display}</span>
              </div>
              
              {/* Server */}
              <div className="text-xs text-muted-foreground mb-2 truncate">
                Server: {table.server_name}
              </div>
              
              {/* Total */}
              <div className="flex items-center justify-between pt-2 border-t border-current/20">
                <span className="text-xs">{table.order_count} orders</span>
                <span className="font-bold">${table.total_amount?.toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table Detail Modal */}
      {selectedTable && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTable(null)}>
          <div 
            className="bg-card rounded-2xl border border-border max-w-2xl w-full max-h-[80vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getStatusColor(selectedTable.overall_status)}`}>
                  <span className="text-xl font-bold">{selectedTable.table_number}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">Table {selectedTable.table_number}</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedTable.guest_count} guests • Seated {selectedTable.time_seated_display}
                  </p>
                </div>
              </div>
              <button onClick={() => setSelectedTable(null)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 max-h-96 overflow-y-auto">
              <div className="mb-4">
                <div className="text-sm text-muted-foreground mb-1">Server</div>
                <div className="font-medium text-foreground">{selectedTable.server_name}</div>
              </div>

              <div className="text-sm font-medium text-muted-foreground mb-2">Orders</div>
              <div className="space-y-3">
                {selectedTable.orders.map((order, i) => (
                  <div key={i} className="bg-muted/30 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-foreground">Order #{order.order_number}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        order.status === 'ready' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                        order.status === 'preparing' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                        'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground mb-2">
                      {order.items?.map(item => `${item.quantity}x ${item.name}`).join(', ')}
                    </div>
                    <div className="text-sm font-medium text-foreground">
                      ${order.total?.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-border flex items-center justify-between">
              <div>
                <div className="text-sm text-muted-foreground">Total Unpaid</div>
                <div className="text-2xl font-bold text-foreground">${selectedTable.total_amount?.toFixed(2)}</div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setSelectedTable(null)}>
                  Close
                </Button>
                <Button 
                  onClick={() => handleCloseTable(selectedTable.table_number)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="close-table-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Mark as Paid
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
